/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../root'
import { WorkflowViewType, Workflow } from './types'
import { WorkflowStatus } from '../../constants/workflow'
import { RcmTable } from '../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../components/table/TableComponents'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { formatUserDateTimeWithPreferences } from '../../utils/commonColumns'
import { Role } from '../../constants/user'
import WorkflowsTableMenu from './WorkflowsTableMenu'
import { useModal } from '../../contexts/ModalContext'
import WorkflowDetailsModal from '../../components/modals/WorkflowDetailsModal'
import TaskDocLink from '../../components/shared/TaskDocLink'
import {
  TableMultiSelectionWrapper,
  SelectAllRows,
  SelectRow,
  TableMultiSelectionConsumer
} from '../../components/rcm-table/TableMultiSelection'
import { formatUserDateTime } from '~/utils/dateFormatting'

interface WorkflowsTableProps {
  viewType: WorkflowViewType
  filterShown: boolean
}

/**
 * Workflows Table Component
 *
 * Displays workflows in a paginated, sortable table with conditional columns
 * based on EOS and ETMF feature flags. Includes multi-select functionality
 * for batch operations on active workflows.
 *
 * Key Features:
 * - Conditional columns based on EOS/ETMF modes
 * - Progress bar visualization for workflow status
 * - Multi-select for batch operations (active view only)
 * - Workflow details modal integration
 * - Actions menu integration
 */
const WorkflowsTable: FC<WorkflowsTableProps> = ({ viewType, filterShown }) => {
  const { t } = useTranslation()
  const { service, api } = useServiceContext()
  const { showModal } = useModal()
  const tableData = useRcmTableData()

  const [isEosView, setIsEosView] = useState(false)
  const isEtmfView = service.init.getFeaturesInfo()?.etmfFeature?.show

  // Get feature flags and user info
  const auth = service?.auth
  const featuresInfo = service?.init?.getFeaturesInfo?.()
  const user = auth?.getUser?.()
  const userRole = auth?.getRole?.()

  // User preferences for date/time formatting
  const userPreferences = {
    dateFormat: user?.dateFormat,
    timeFormat: user?.timeFormat,
    timeFormat24h: user?.timeFormat24h
  }

  // Determine EOS and ETMF views based on filter and feature flags
  useEffect(() => {
    const eosFeatureEnabled = featuresInfo?.eosFeature?.show ?? false

    const eosView = eosFeatureEnabled && (userRole === Role.USER || tableData.filter?.isEosSelected)

    setIsEosView(eosView)
  }, [tableData.entries, tableData.filter, featuresInfo, userRole])

  /**
   * Get current phase display text based on workflow status
   */
  const getCurrentPhase = (workflow: Workflow): string => {
    if (workflow.status === WorkflowStatus.CANCELLED) {
      return t('WORKFLOWS_STEP_STATUS_CANCELLED')
    } else if (workflow.status === WorkflowStatus.COMPLETED) {
      return t('WORKFLOWS_STEP_STATUS_FINISHED')
    }
    return workflow.currentPhase || ''
  }

  /**
   * Format date/time with user preferences
   */
  const formatDateTime = (timestamp: number | null): string => {
    if (!timestamp) return t('NOT_AVAILABLE')
    return formatUserDateTimeWithPreferences(timestamp, true, userPreferences)
  }

  /**
   * Handle workflow row click to open details modal
   */
  const handleRowClick = (workflow: Workflow) => {
    showModal(<WorkflowDetailsModal wfId={workflow.id} />)
  }

  /**
   * Handle keyboard events for accessible interactions
   */
  const handleKeyDown = (event: React.KeyboardEvent, callback: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      callback()
    }
  }

  const tableContent = (
    <RcmTable
      className="workflow-list-table chita-table table-condensed"
      noResultsText="NO_RESULTS"
      disableOnInitLoad={filterShown}
      HeadRow={
        <TableHeadRow className={viewType === WorkflowViewType.ACTIVE ? 'has-actions' : ''}>
          {/* Multi-select checkbox column (active view only) */}
          {viewType === WorkflowViewType.ACTIVE && (
            <th className="clipped-cell with-checkbox" id="workflow-select-all">
              <SelectAllRows />
            </th>
          )}

          {/* ID column */}
          <TableHeadCell sort="ID">{t('NUMBER')}</TableHeadCell>

          {/* EOS columns */}
          {isEosView && (
            <>
              <TableHeadCell id="eos-study" style={{ fontSize: '12px' }}>
                {t('EMTF_MD_STUDY')}
              </TableHeadCell>
              <TableHeadCell id="eos-site" style={{ fontSize: '12px' }}>
                {t('EMTF_MD_SITE')}
              </TableHeadCell>
            </>
          )}

          {/* Type column */}
          <TableHeadCell sort="TYPE">{t('TYPE')}</TableHeadCell>

          {/* Status/Progress column */}
          <TableHeadCell id="status" style={{ fontSize: '12px' }}>
            {t('STATUS')}
          </TableHeadCell>

          {/* Document name (not shown for EOS) */}
          {!isEosView && (
            <TableHeadCell id="document-name" style={{ fontSize: '12px' }}>
              {t('DOCUMENT_NAME')}
            </TableHeadCell>
          )}

          {/* Start date */}
          <TableHeadCell sort="START_DATE" style={{ width: '190px' }}>
            {t('START_DATE')}
          </TableHeadCell>

          {/* Initiator and Current Phase (not shown for ETMF) */}
          {!isEtmfView && (
            <>
              <TableHeadCell sort="INITIATOR" style={{ fontSize: '12px' }}>
                {t('INITIATOR')}
              </TableHeadCell>
              <TableHeadCell id="current-phase" style={{ fontSize: '12px' }}>
                {t('CURRENT_PHASE')}
              </TableHeadCell>
            </>
          )}

          {/* ETMF columns */}
          {isEtmfView && (
            <>
              <TableHeadCell id="etmf-study" style={{ fontSize: '12px' }}>
                {t('EMTF_MD_STUDY')}
              </TableHeadCell>
              <TableHeadCell id="etmf-country" style={{ fontSize: '12px' }}>
                {t('EMTF_MD_COUNTRY')}
              </TableHeadCell>
              <TableHeadCell id="etmf-site" style={{ fontSize: '12px' }}>
                {t('REPORTS_FILTER_LABEL_SITE')}
              </TableHeadCell>
            </>
          )}

          {/* Actions column */}
          <TableHeadCell id="actions" className="actions" style={{ fontSize: '12px' }}>
            {t('ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={({ row }: { row: any }) => {
        const workflow = row as Workflow

        return (
          <TableRow key={workflow.id}>
            {/* Multi-select checkbox (active view only) */}
            {viewType === WorkflowViewType.ACTIVE && (
              <td className="clipped-cell with-checkbox">
                <SelectRow row={{ id: String(workflow.id) }} disabled={false} />
              </td>
            )}

            {/* ID - clickable to open details */}
            <td style={{ paddingLeft: '0' }}>
              <button className="btn btn-link" onClick={() => handleRowClick(workflow)} title={workflow.formattedId}>
                {workflow.formattedId}
              </button>
            </td>

            {/* EOS columns */}
            {isEosView && (
              <>
                <td>{workflow.study?.name ?? '-'}</td>
                <td>{workflow.site?.name ?? '-'}</td>
              </>
            )}

            {/* Type - clickable to open details */}
            <td style={{ paddingLeft: '0' }}>
              <button
                className="btn btn-link word-break"
                onClick={() => handleRowClick(workflow)}
                title={workflow.localizedType}
              >
                {workflow.localizedType}
              </button>
            </td>

            {/* Status with progress bar */}
            <td className="status">
              <div
                onClick={() => handleRowClick(workflow)}
                onKeyDown={(e) => handleKeyDown(e, () => handleRowClick(workflow))}
                role="button"
                tabIndex={0}
                className={`status-wrapper${
                  workflow.status === WorkflowStatus.CANCELLED ||
                  workflow.status === WorkflowStatus.INACTIVE ||
                  workflow.rejected
                    ? ' canceled'
                    : ''
                }`}
              >
                <span style={{ width: `${workflow.progress}%` }} className={workflow.progress === 100 ? 'w100p' : ''} />
              </div>
            </td>

            {/* Document name (not shown for EOS) */}
            {!isEosView && (
              <td className="doc-name clipped-cell">
                <TaskDocLink task={workflow as any} isWorkflow={true} />
              </td>
            )}

            {/* Start date */}
            <td>{formatUserDateTime(workflow.startDate)}</td>

            {/* Initiator and Current Phase (not shown for ETMF) */}
            {!isEtmfView && (
              <>
                <td>{workflow.initiator?.name ?? t('NOT_AVAILABLE')}</td>
                <td>{getCurrentPhase(workflow)}</td>
              </>
            )}

            {/* ETMF columns */}
            {isEtmfView && (
              <>
                <td>{workflow.filteredFileToEtmfDocDetails?.study ?? '-'}</td>
                <td>{workflow.filteredFileToEtmfDocDetails?.country ?? '-'}</td>
                <td>{workflow.filteredFileToEtmfDocDetails?.site ?? '-'}</td>
              </>
            )}

            {/* Actions menu */}
            <td className="actions">
              <TableMultiSelectionConsumer>
                {(tableMultiSelection) => {
                  return (
                    <WorkflowsTableMenu
                      workflow={workflow}
                      multiSelectOptions={{
                        selectedIds: tableMultiSelection?.multiSelectState?.selectedIds?.map((id) => Number(id)) || [],
                        selectedWholeResult: tableMultiSelection?.multiSelectState?.selectedWholeResult || false,
                        selectedItems: tableMultiSelection?.multiSelectState?.selectedIds
                          ? (tableData.entries.filter((wf: any) =>
                              tableMultiSelection.multiSelectState.selectedIds.includes(String(wf.id))
                            ) as Workflow[])
                          : [],
                        deselectAll: tableMultiSelection?.deselectAll
                      }}
                    />
                  )
                }}
              </TableMultiSelectionConsumer>
            </td>
          </TableRow>
        )
      }}
    />
  )

  return (
    <div className="workflows-table">
      {viewType === WorkflowViewType.ACTIVE ? (
        <TableMultiSelectionWrapper
          texts={{
            selectAllItemsText: '',
            allItemsSelectedText: 'DOCUMENTS_MULTISELECT_ALL_N_ITEMS_SELECTED'
          }}
          isWorkflowCancelled={true}
          isWorkflow={true}
          getTotalNumberFunction={() => {
            return api?.workflows?.workflows
              ?.getList({
                pageSize: 20,
                pageStart: 0,
                sortBy: 'START_DATE',
                sortDesc: true
              })
              .then((data: any) => {
                return data
              })
          }}
        >
          {tableContent}
        </TableMultiSelectionWrapper>
      ) : (
        tableContent
      )}
    </div>
  )
}

export default WorkflowsTable
